import {
  DayAvailability,
  DAYS_OPTIONS,
  TimeSlot,
  Weekday,
} from "@/lib/types/availability";
import { RRule, Weekday as RRuleWeekday } from "rrule";

/**
 * Storage shape of a single recurring weekly availability slot. Intentionally
 * narrow: only weekday + time range, since the booking window (how far into
 * the future clients can book) is a sliding property of the service, not a
 * property of the slot itself.
 */
export interface AvailableSlot {
  weekday: Weekday;
  startTime: string; // "HH:mm"
  endTime: string; // "HH:mm"
}

/**
 * Encodes availability for persistence. Each returned string is a JSON object
 * describing a single weekly slot. We keep `string[]` to preserve the column
 * schema while removing the previous RRULE-based encoding that abused the
 * `UNTIL` property to carry end-of-day time information.
 */
export function encodeAvailability(availability: DayAvailability[]): string[] {
  const encoded: string[] = [];
  for (const dayConfig of availability) {
    if (!dayConfig.enabled || dayConfig.slots.length === 0) continue;
    for (const slot of dayConfig.slots) {
      const entry: AvailableSlot = {
        weekday: dayConfig.day,
        startTime: slot.startTime,
        endTime: slot.endTime,
      };
      encoded.push(JSON.stringify(entry));
    }
  }
  return encoded;
}

/**
 * Decodes stored availability into the form-friendly {@link DayAvailability}
 * structure. Supports the current JSON encoding and the legacy RRULE encoding
 * for back-compat with rows written before this module was refactored.
 */
export function decodeAvailability(
  slots: string[] | null | undefined,
): DayAvailability[] {
  const availability: DayAvailability[] = (
    Object.keys(DAYS_OPTIONS) as Weekday[]
  ).map((day) => ({ day, enabled: false, slots: [] }));

  if (!slots || slots.length === 0) return availability;

  for (const raw of slots) {
    const slot = parseSlot(raw);
    if (!slot) continue;

    const dayIndex = availability.findIndex((d) => d.day === slot.weekday);
    if (dayIndex === -1) continue;

    availability[dayIndex].enabled = true;
    availability[dayIndex].slots.push({
      startTime: slot.startTime,
      endTime: slot.endTime,
    });
  }

  return availability;
}

/**
 * Returns the structured availability slots (flat) used by booking
 * calculations. Falls back to an empty list on invalid entries.
 */
export function getAvailableSlots(
  slots: string[] | null | undefined,
): AvailableSlot[] {
  if (!slots || slots.length === 0) return [];
  const parsed: AvailableSlot[] = [];
  for (const raw of slots) {
    const slot = parseSlot(raw);
    if (slot) parsed.push(slot);
  }
  return parsed;
}

/**
 * Checks if two time slots overlap.
 */
function doTimeSlotsOverlap(slot1: TimeSlot, slot2: TimeSlot): boolean {
  const [start1Hour, start1Min] = slot1.startTime.split(":").map(Number);
  const [end1Hour, end1Min] = slot1.endTime.split(":").map(Number);
  const [start2Hour, start2Min] = slot2.startTime.split(":").map(Number);
  const [end2Hour, end2Min] = slot2.endTime.split(":").map(Number);

  const start1 = start1Hour * 60 + start1Min;
  const end1 = end1Hour * 60 + end1Min;
  const start2 = start2Hour * 60 + start2Min;
  const end2 = end2Hour * 60 + end2Min;

  return start1 < end2 && start2 < end1;
}

/**
 * Validates that time slots don't conflict with each other.
 * Returns an error message if there are conflicts, null otherwise.
 */
export function validateAvailabilitySlots(
  availability: DayAvailability[],
): string | null {
  for (const dayConfig of availability) {
    if (!dayConfig.enabled || dayConfig.slots.length === 0) continue;

    for (let i = 0; i < dayConfig.slots.length; i++) {
      const slot = dayConfig.slots[i];

      const [startHour, startMin] = slot.startTime.split(":").map(Number);
      const [endHour, endMin] = slot.endTime.split(":").map(Number);

      const startMinutes = startHour * 60 + startMin;
      const endMinutes = endHour * 60 + endMin;

      if (startMinutes >= endMinutes) {
        return `Invalid time slot on ${dayConfig.day}: start time must be before end time (${slot.startTime} - ${slot.endTime})`;
      }

      for (let j = i + 1; j < dayConfig.slots.length; j++) {
        const otherSlot = dayConfig.slots[j];
        if (doTimeSlotsOverlap(slot, otherSlot)) {
          return `Overlapping time slots on ${DAYS_OPTIONS[dayConfig.day]}: ${slot.startTime}-${slot.endTime} conflicts with ${otherSlot.startTime}-${otherSlot.endTime}`;
        }
      }
    }
  }
  return null;
}

/**
 * Checks if availability has at least one enabled day with slots.
 */
export function hasAvailableSlots(availability: DayAvailability[]): boolean {
  return availability.some((day) => day.enabled && day.slots.length > 0);
}

// Internal helpers --------------------------------------------------------

const JSON_WEEKDAYS = new Set<Weekday>(
  Object.keys(DAYS_OPTIONS) as Weekday[],
);

const RRULE_WEEKDAY_TO_NAME: Record<number, Weekday> = {
  [RRule.MO.weekday]: "monday",
  [RRule.TU.weekday]: "tuesday",
  [RRule.WE.weekday]: "wednesday",
  [RRule.TH.weekday]: "thursday",
  [RRule.FR.weekday]: "friday",
  [RRule.SA.weekday]: "saturday",
  [RRule.SU.weekday]: "sunday",
};

function parseSlot(raw: string): AvailableSlot | null {
  if (raw.trimStart().startsWith("{")) {
    return parseJsonSlot(raw);
  }
  // Legacy encoding: RRULE string with DTSTART/UNTIL encoding slot times.
  return parseRRuleSlot(raw);
}

function parseJsonSlot(raw: string): AvailableSlot | null {
  try {
    const parsed = JSON.parse(raw) as Partial<AvailableSlot>;
    const { weekday, startTime, endTime } = parsed;
    if (!isWeekday(weekday)) return null;
    if (!isTimeString(startTime) || !isTimeString(endTime)) return null;
    return { weekday, startTime, endTime };
  } catch {
    return null;
  }
}

function parseRRuleSlot(raw: string): AvailableSlot | null {
  try {
    const rule = RRule.fromString(raw);
    const byweekday = rule.options.byweekday;
    if (!byweekday || byweekday.length === 0) return null;

    const weekdayNum = extractWeekdayNumber(byweekday[0]);
    if (weekdayNum === null) return null;

    const weekday = RRULE_WEEKDAY_TO_NAME[weekdayNum];
    if (!weekday) return null;

    const { dtstart, until } = rule.options;
    if (!dtstart || !until) return null;

    return {
      weekday,
      startTime: formatClockTime(dtstart.getHours(), dtstart.getMinutes()),
      endTime: formatClockTime(until.getHours(), until.getMinutes()),
    };
  } catch {
    return null;
  }
}

function extractWeekdayNumber(value: unknown): number | null {
  if (typeof value === "number") return value;
  if (value && typeof value === "object" && "weekday" in value) {
    return (value as RRuleWeekday).weekday;
  }
  return null;
}

function isWeekday(value: unknown): value is Weekday {
  return typeof value === "string" && JSON_WEEKDAYS.has(value as Weekday);
}

function isTimeString(value: unknown): value is string {
  return typeof value === "string" && /^\d{2}:\d{2}$/.test(value);
}

function formatClockTime(hour: number, minute: number): string {
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}
